import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import stores from "../../store/stores";
import {useTranslation} from "react-i18next";
import {Col, Row, Table, Tabs, Tag} from "antd";
import UserGroupModal from "./modals/UserGroupModal";
import {SearchOutlined, PlusOutlined} from "@ant-design/icons";
import {SearchInput} from "./userPageStyles";
import PermissionPolicyModal from "./modals/PermissionPolicyModal";
import {useFetch} from "../../hooks/useFetch";
import {getLocaleLang} from "../../utils/helpers";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";

const AuthorizationManagement = () => {
    const {TabPane} = Tabs;

    const [activeKey, setActiveKey] = useState("1");
    const [permissionPolicies, setPermissionPolicies] = useState();
    const [userPermissionPolicies, setUserPermissionPolicies] = useState();
    const [userGroupModalVisibility, setUserGroupModalVisibility] = useState(false);
    const [permissionPolicyModalVisibility, setPermissionPolicyModalVisibility] = useState(false);
    const [recallUserGroup, setRecallUserGroup] = useState(false);
    const [recallPermissionPolicy, setRecallPermissionPolicy] = useState(false);

    const [modalTitle, setModalTitle] = useState("");
    const {t, i18n} = useTranslation(["userAuthorization"]);

    const [userGroupLoading, userGroup, ,] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.authorizationStore.listUserGroups.bind(null, {}),
        dependencies: [recallUserGroup]
    });
    const [permissionPoliciesDataLoading, permissionPoliciesData, ,] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.authorizationStore.listPermissionPolicies.bind(null, {}),
        dependencies: [recallPermissionPolicy]
    });

    const changeTab = value => {
        setActiveKey(value);
    };

    const handleUserGroupCreateModalPress = () => {
        setUserPermissionPolicies();
        setModalTitle(t("modals.userGroupCreateModal.label", {ns: "userAuthorization"}));
        setUserGroupModalVisibility(true);
    };
    const handleUserGroupDetailModalPress = item => {
        setModalTitle(t("modals.userGroupDetailModal.label", {ns: "userAuthorization"}));
        setUserGroupModalVisibility(true);
        setUserPermissionPolicies(item);
    };

    const handlePermissionPolicyModalPress = () => {
        setPermissionPolicies();
        setModalTitle(t("modals.permissionPolicyCreateModal.label", {ns: "userAuthorization"}));
        setPermissionPolicyModalVisibility(true);
    };

    const handlePermissionPolicyDetailModalPress = item => {
        setModalTitle(t("modals.permissionPolicyDetailModal.label", {ns: "userAuthorization"}));
        setPermissionPolicyModalVisibility(true);
        setPermissionPolicies(item);
    };

    const onCancelUserGroupModal = () => {
        setUserGroupModalVisibility(false);
        setUserPermissionPolicies();
    };

    return useObserver(() => (
        <React.Fragment>
            <UserGroupModal
                visibility={userGroupModalVisibility}
                modalTitle={modalTitle}
                onCancel={onCancelUserGroupModal}
                detail={userPermissionPolicies}
                allPermissionPolicies={permissionPoliciesData?.rows}
                setRecallUserGroup={setRecallUserGroup}
            />
            <PermissionPolicyModal
                visibility={permissionPolicyModalVisibility}
                modalTitle={modalTitle}
                onCancel={() => setPermissionPolicyModalVisibility(false)}
                detail={permissionPolicies}
                allPermissionPoliciesData={permissionPoliciesData?.rows}
                setRecallPermissionPolicy={setRecallPermissionPolicy}
            />

            <Row type="flex" justify="space-between">
                <Col span={12}>
                    <Tabs activeKey={activeKey} onChange={changeTab}>
                        <TabPane tab={t("tabs.userGroup", {ns: "userAuthorization"})} key="1" />
                        <TabPane
                            tab={t("tabs.permissionPolicies", {ns: "userAuthorization"})}
                            key="2"
                        />
                    </Tabs>
                </Col>
                <Col span={12}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            marginTop: 10
                        }}
                    >
                        {/* <SearchInput icon={<SearchOutlined />} /> */}
                        {activeKey === "1" ? (
                            <CustomButton
                                onClick={handleUserGroupCreateModalPress}
                                icon={<PlusOutlined />}
                            >
                                {t("components.newUserGroupButton.label", {
                                    ns: "userAuthorization"
                                })}
                            </CustomButton>
                        ) : (
                            <CustomButton
                                onClick={handlePermissionPolicyModalPress}
                                icon={<PlusOutlined />}
                            >
                                {t("components.newPermissionPolicyButton.label", {
                                    ns: "userAuthorization"
                                })}
                            </CustomButton>
                        )}
                    </div>
                </Col>
            </Row>

            {activeKey === "1" ? (
                <Table
                    rowKey="_id"
                    loading={userGroupLoading}
                    dataSource={userGroup?.rows}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Table.Column
                        dataIndex="name"
                        title={t("components.userGroupList.fields.name", {ns: "userAuthorization"})}
                        render={name => <p>{name[getLocaleLang(i18n)]}</p>}
                    />

                    <Table.Column
                        align="right"
                        title={t("actions.action", {ns: "common"})}
                        render={item => (
                            <a onClick={() => handleUserGroupDetailModalPress(item)}>
                                {t("actions.detail", {ns: "common"})}
                            </a>
                        )}
                    />
                </Table>
            ) : (
                <Table
                    rowKey="_id"
                    loading={permissionPoliciesDataLoading}
                    dataSource={permissionPoliciesData?.rows}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Table.Column
                        dataIndex="name"
                        title={t("components.permissionPolicyList.fields.name", {
                            ns: "userAuthorization"
                        })}
                        render={name => <p>{name[getLocaleLang(i18n)]}</p>}
                    />

                    <Table.Column
                        dataIndex="description"
                        title={t("components.permissionPolicyList.fields.description", {
                            ns: "userAuthorization"
                        })}
                        render={description => (
                            <span data-testid="description">
                                {description[getLocaleLang(i18n)]}
                            </span>
                        )}
                    />
                    <Table.Column
                        align="center"
                        dataIndex="tags"
                        title={t("components.permissionPolicyList.fields.tags", {
                            ns: "userAuthorization"
                        })}
                        render={tags =>
                            tags.map(tag => (
                                <Tag color="blue" key={tag}>
                                    {tag}
                                </Tag>
                            ))
                        }
                    />

                    <Table.Column
                        align="right"
                        title={t("actions.action", {ns: "common"})}
                        render={item => (
                            <a onClick={() => handlePermissionPolicyDetailModalPress(item)}>
                                {t("actions.detail", {ns: "common"})}
                            </a>
                        )}
                    />
                </Table>
            )}
        </React.Fragment>
    ));
};

export default AuthorizationManagement;
